import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { SPEECHES_WORDS_IN_PLAN } from '@/constants/common';

import { CHARACTERS_PER_GENERATION, useMusicT } from './kit';
import type { MusicFaq as Faq, MusicLandingPage } from './types';

/** Questions every music page answers, after the page's own. */
const SHARED_FAQS: Faq[] = [
  {
    question: 'Is it free?',
    answer:
      'Yes, you can try it for free. The Free plan makes one song a month, in two versions. Paid plans make many more, from {{starter}} songs a month on Starter.',
  },
  {
    question: 'Can it make songs with vocals and lyrics?',
    answer:
      'Yes. Describe the song and the AI writes the lyrics and sings them, or turn on Custom Mode and paste your own. Pick a male or female voice, or tick Instrumental Only for music without singing.',
  },
  {
    question: 'How long are the songs?',
    answer:
      'Most songs are two to four minutes long. You can’t set an exact length, so trim the song in an audio or video editor when you need a shorter clip.',
  },
  {
    question: 'Can I use the songs commercially?',
    answer:
      'Yes, on a paid plan. Songs you make with a paid subscription come with full commercial rights, and you keep them after the subscription ends. Songs made on the Free plan are for non-commercial use.',
  },
  {
    question: 'Which file formats can I download?',
    answer:
      'MP3, for sharing and listening, and WAV, uncompressed for editing and mastering. Each song also comes with its own cover art.',
  },
  {
    question: 'Why do I get two versions?',
    answer:
      'Every generation makes two different takes of your idea, so you can keep the one you like best. Both count as one song.',
  },
  {
    question: 'Which languages can it sing in?',
    answer:
      'English and many other languages, such as Spanish, Portuguese, French, German, Japanese and Korean. Write your description or lyrics in the language you want the song sung in.',
  },
];

/**
 * The page's own questions first, then the shared ones. Rendered as FAQPage
 * structured data too.
 */
export default function MusicFaq({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const vars = {
    starter: Math.floor(
      SPEECHES_WORDS_IN_PLAN.Starter / CHARACTERS_PER_GENERATION,
    ),
  };

  const seen = new Set<string>();
  const faqs = [...page.faqs, ...SHARED_FAQS]
    .filter((faq) => {
      if (seen.has(faq.question)) return false;
      seen.add(faq.question);
      return true;
    })
    .map((faq) => ({
      question: t(faq.question, vars),
      answer: t(faq.answer, vars),
    }));

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading size='md' title={t('Questions and answers.')} />
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }, index) => (
              <li key={question}>
                <details className='group' open={index === 0}>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    <h3 className='text-lg font-semibold'>{question}</h3>
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
